import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Calendar, Clock, UserCheck, AlertCircle, CheckCircle, XCircle,
  Search, Download, Wifi, WifiOff, Edit2, Trash2, List, Activity,
  X, SlidersHorizontal, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Filter, XCircle as FilterX,
} from 'lucide-react';

import { workforceApi } from '../lib/workforce';
import { useAuth } from '../lib/auth';
import { Toaster } from 'react-hot-toast';
import {
  notify, toastLite, notifySuccess, notifyError, notifyWarn, notifyInfo,
  notifyPromise, notifyAction, dismiss
} from '../lib/notify.jsx';

import { getErrorMessage } from '../lib/errorMessage.js';
import { toSlug } from '../utils/slug.js';
import styles from '../styles/horarios.js';
import { twoInitials } from '../utils/text';

const API_BASE = 'https://hrs-api.distritec.cloud/api';

function playAlertSound() {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(660, audioCtx.currentTime);
    gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
    oscillator.start();
    oscillator.stop(audioCtx.currentTime + 0.3);
  } catch (e) {
    console.warn('No se pudo reproducir alerta sonora:', e);
  }
}

const formatearTiempo = (segundos) => {
  if (!segundos) return '0s';
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segs = segundos % 60;
  if (horas > 0) return `${horas}h ${minutos}m`;
  if (minutos > 0) return `${minutos}m ${segs}s`;
  return `${segs}s`;
};

// ─── TiempoEnEstado mejorado ────────────────────────────────────────────────
// Recibe inicioEstado (del WS o de la API REST).
// Si inicioEstado es null, intenta cargarlo desde /api/asesores/{idAsesor}/status/
function TiempoEnEstado({ inicioEstado, idAsesor, estadoActual }) {
  const calcularDiferencia = (inicio) => {
    if (!inicio) return 0;
    const inicioMs = new Date(inicio).getTime();
    if (isNaN(inicioMs)) return 0;
    const diff = Math.floor((Date.now() - inicioMs) / 1000);
    return diff > 0 ? diff : 0;
  };

  // Fuente real del inicio: WS o API
  const [inicioResuelto, setInicioResuelto] = useState(inicioEstado || null);
  const [tiempo, setTiempo] = useState(() => calcularDiferencia(inicioEstado));
  const hasCargadoRef = useRef(false);

  // Si cambia el inicio desde el WS, actualizar
  useEffect(() => {
    if (inicioEstado) {
      setInicioResuelto(inicioEstado);
      setTiempo(calcularDiferencia(inicioEstado));
      hasCargadoRef.current = true;
    }
  }, [inicioEstado]);

  // Si no hay inicio desde el WS, pedir a la API REST
  useEffect(() => {
    if (inicioEstado || hasCargadoRef.current || !idAsesor) return;

    let cancelado = false;

    const cargar = async () => {
      try {
        const res = await fetch(`${API_BASE}/asesores/${idAsesor}/status/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            'Content-Type': 'application/json',
          }
        });
        if (!res.ok) return;
        const data = await res.json();

        if (cancelado) return;

        // Solo usar si el estado coincide con el estado actual del WS
        const estadoApi = (data?.estado || '').toLowerCase();
        const estadoWs  = (estadoActual || '').toLowerCase();

        if (data?.inicio && (estadoApi === estadoWs || !estadoWs)) {
          hasCargadoRef.current = true;
          setInicioResuelto(data.inicio);
          setTiempo(calcularDiferencia(data.inicio));
        }
      } catch (e) {
        // silencioso — la API puede no estar disponible
      }
    };

    cargar();
    return () => { cancelado = true; };
  }, [idAsesor, inicioEstado, estadoActual]);

  // Tick cada segundo
  useEffect(() => {
    if (!inicioResuelto) return;
    const id = setInterval(() => {
      setTiempo(calcularDiferencia(inicioResuelto));
    }, 1000);
    return () => clearInterval(id);
  }, [inicioResuelto]);

  const formatear = (t) => {
    const h = String(Math.floor(t / 3600)).padStart(2, "0");
    const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
    const s = String(t % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  if (!inicioResuelto && !idAsesor) return <span>00:00:00</span>;

  return (
    <span title={inicioResuelto ? `Desde: ${new Date(inicioResuelto).toLocaleTimeString('es-CO')}` : 'Cargando...'}>
      {formatear(tiempo)}
    </span>
  );
}

export default function AsignarHorarios() {
  const { user } = useAuth({ requireAuth: true });
  const [activeTab, setActiveTab] = useState('control');
  const [asesoresConectados, setAsesoresConectados] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [horariosCreados, setHorariosCreados] = useState([]);
  const [loading, setLoading] = useState(false);
  // Estado para filtros y paginación de horarios
  const [filtroHorarioAsesor, setFiltroHorarioAsesor] = useState('');
  const [filtroHorarioFecha, setFiltroHorarioFecha] = useState('');
  const [filtroHorarioOrden, setFiltroHorarioOrden] = useState('recientes');
  const [paginaActual, setPaginaActual] = useState(1);
  const [horariosPorPagina] = useState(9);
  const [filtroArea, setFiltroArea] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const wsRef = useRef(null);
  const [wsConectado, setWsConectado] = useState(false);
  const [error, setError] = useState(null);
  const [asesores, setAsesores] = useState([]);
  const [cargandoAsesores, setCargandoAsesores] = useState(false);
  const [errorAsesores, setErrorAsesores] = useState(null);
  const [sedeSeleccionada, setSedeSeleccionada] = useState('');
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [busquedaSede, setBusquedaSede] = useState('');
  const [busquedaAsesor, setBusquedaAsesor] = useState('');
  const [selectSedeAbierto, setSelectSedeAbierto] = useState(false);
  const [selectAsesorAbierto, setSelectAsesorAbierto] = useState(false);
  const sedeDropdownRef = useRef(null);
  const asesorDropdownRef = useRef(null);

  // Estados para el selector de asesor con búsqueda y filtro por sede (configuración personalizada)
  const [cfgSedeSeleccionada, setCfgSedeSeleccionada] = useState('');
  const [cfgBusquedaSede, setCfgBusquedaSede] = useState('');
  const [cfgBusquedaAsesor, setCfgBusquedaAsesor] = useState('');
  const [cfgSelectSedeAbierto, setCfgSelectSedeAbierto] = useState(false);
  const [cfgSelectAsesorAbierto, setCfgSelectAsesorAbierto] = useState(false);
  const cfgSedeDropdownRef = useRef(null);
  const cfgAsesorDropdownRef = useRef(null);

  const [jornadasAsesores, setJornadasAsesores] = useState({});
  const [estadosDisponibles, setEstadosDisponibles] = useState({});
  const [horariosActuales, setHorariosActuales] = useState({});
  // statusPorApi ya NO se necesita aquí — TiempoEnEstado lo maneja internamente

  const [asesorSeleccionado, setAsesorSeleccionado] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [horaEntrada, setHoraEntrada] = useState('09:00');
  const [horaSalida, setHoraSalida] = useState('18:00');
  const [diasSemana, setDiasSemana] = useState({
    lunes: true, martes: true, miercoles: true, jueves: true,
    viernes: true, sabado: false, domingo: false
  });
  const [minutosAdicionales, setMinutosAdicionales] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [horarioEditando, setHorarioEditando] = useState(null);
  const [estadoTipos, setEstadoTipos] = useState([]);
  const [estadoConfigs, setEstadoConfigs] = useState([]);
  const [nuevoEstadoId, setNuevoEstadoId] = useState('');
  const [nuevoMinLim, setNuevoMinLim] = useState('');
  const [nuevoColor, setNuevoColor] = useState('');
  const [usarColorOverride, setUsarColorOverride] = useState(false);
  const [fallosEstados, setFallosEstados] = useState(new Set());

  // Filtrar sedes para la configuración personalizada
  const cfgSedesFiltradas = useMemo(() => {
    if (!cfgBusquedaSede) return sedesDisponibles;
    const term = cfgBusquedaSede.toLowerCase();
    return sedesDisponibles.filter(s =>
      (s.nombre_sede || '').toLowerCase().includes(term)
    );
  }, [sedesDisponibles, cfgBusquedaSede]);

  // Filtrar asesores por sede y búsqueda para la configuración personalizada
  const cfgAsesoresPorSede = useMemo(() => {
    if (!cfgSedeSeleccionada) return asesores;
    return asesores.filter(a => String(a.id_sede) === String(cfgSedeSeleccionada));
  }, [asesores, cfgSedeSeleccionada]);

  const cfgAsesoresFiltrados = useMemo(() => {
    if (!cfgBusquedaAsesor) return cfgAsesoresPorSede;
    const term = cfgBusquedaAsesor.toLowerCase();
    return cfgAsesoresPorSede.filter(a =>
      (a.nombre || '').toLowerCase().includes(term) ||
      (a.cargo || '').toLowerCase().includes(term) ||
      (a.nombre_sede || '').toLowerCase().includes(term)
    );
  }, [cfgAsesoresPorSede, cfgBusquedaAsesor]);

  function extractEstadoSlug(raw) {
    if (!raw) return 'desconectado';
    if (typeof raw === 'string') return raw.toLowerCase();
    if (typeof raw === 'object') {
      const candidate = raw.slug || raw.codigo || raw.code || raw.estado || raw.nombre || raw.name;
      if (candidate) return String(candidate).toLowerCase();
    }
    if (typeof raw === 'number') return String(raw);
    return 'desconectado';
  }

  const asesorPerteneceASede = (asesorId) => {
    if (user?.rol === 'Lider talento humano') return true;
    if (user?.cargo_id === 22) return true;  

    if (asesores.length === 0) return true;
    const id = asesorId || null;
    if (id == null) return false;
    return idsDeSedeSet.has(id) || idsDeSedeSet.has(String(id)) || idsDeSedeSet.has(Number(id));
  };

  const normalizarAsesor = (raw) => ({
    id_asesor: raw.id_asesor ?? raw.ID_Asesor ?? raw.id ?? raw.userId,
    nombre: raw.nombre ?? raw.Nombre ?? raw.usuario ?? raw.name ?? '',
    cargo: raw.cargo ?? raw.Cargo ?? raw.cargo_id ?? '',
    area: raw.area ?? raw.Area ?? '',
    id_sede: raw.id_sede ?? raw.ID_Sede ?? null,
    nombre_sede: raw.nombre_sede ?? raw.Name_Sede ?? null,
  });

  const estiloEstadosList = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 };
  const estiloChip = {
    fontSize: 11, fontFamily: "'Poppins', sans-serif", padding: '3px 8px',
    borderRadius: 999, border: '1px solid var(--accent)', background: 'var(--accent)',
    color: '#4b5563', marginTop: 2,
  };
  const estiloChipActivo = { background: 'var(--accent)', color: 'var(--surface)', fontWeight: 600 };
  const estiloPuntualidad = {
    display: 'inline-flex', alignItems: 'center', padding: '4px 8px',
    borderRadius: 999, fontSize: 11, fontFamily: "'Poppins', sans-serif",
    fontWeight: 600, marginTop: 4,
  };

  useEffect(() => {
    if (!user?.id) return;
    cargarSolicitudes();
    cargarHorarios();
  }, [user?.id]);

  useEffect(() => {
    if (user?.id_sedes) {
      cargarAsesoresPorSede(user.id_sedes);
    } else {
      setErrorAsesores('No hay id_sedes en el usuario para cargar asesores.');
      setAsesores([]);
    }
  }, [user?.id_sedes]);

  useEffect(() => {
    (async () => {
      try {
        const tipos = await workforceApi.getEstadoTipos();
        const lista = Array.isArray(tipos) ? tipos : (tipos?.results || []);
        setEstadoTipos(lista);
      } catch (e) {
        console.error('[Estados] No se pudo cargar catálogo:', e);
      }
    })();
  }, []);

  const estadosCatalogoIndex = useMemo(() => {
    const idx = {};
    (estadoTipos || []).forEach(e => {
      const key = (e.slug || e.codigo || e.nombre || '').toString().toLowerCase();
      if (key) idx[key] = e;
    });
    return idx;
  }, [estadoTipos]);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sedeDropdownRef.current && !sedeDropdownRef.current.contains(event.target)) {
        setSelectSedeAbierto(false);
      }
      if (asesorDropdownRef.current && !asesorDropdownRef.current.contains(event.target)) {
        setSelectAsesorAbierto(false);
      }
      // Cerrar dropdowns de configuración personalizada
      if (cfgSedeDropdownRef.current && !cfgSedeDropdownRef.current.contains(event.target)) {
        setCfgSelectSedeAbierto(false);
      }
      if (cfgAsesorDropdownRef.current && !cfgAsesorDropdownRef.current.contains(event.target)) {
        setCfgSelectAsesorAbierto(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const explicit = import.meta.env.VITE_WS_URL;
    const fallback = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/workforce/`;
    const WS_URL = explicit || fallback;

    let ws;
    let heartbeatInterval;
    let inactivityWarningTimeout;
    let reconnectTimeout;
    let reconnectAttempt = 0;
    let destroyed = false;

    const clearTimers = () => {
      clearInterval(heartbeatInterval);
      clearTimeout(inactivityWarningTimeout);
      clearTimeout(reconnectTimeout);
    };

    const scheduleReconnect = () => {
      if (destroyed) return;
      const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempt));
      reconnectAttempt += 1;
      reconnectTimeout = setTimeout(() => { connectWS(); }, delay);
    };

    const connectWS = () => {
      try {
        ws = new WebSocket(WS_URL);
      } catch (e) {
        console.error('[WS] Error al crear WebSocket:', e);
        scheduleReconnect();
        return;
      }

      wsRef.current = ws;

      ws.onopen = () => {
        if (destroyed) { try { ws.close(); } catch {} return; }
        setWsConectado(true);
        setError(null);
        reconnectAttempt = 0;

        ws.send(JSON.stringify({
          type: 'identify_leader',
          userId: user?.id,
          nombre: user?.nombre,
          cargo: user?.cargo
        }));
        ws.send(JSON.stringify({ type: 'request_all_status' }));

        clearInterval(heartbeatInterval);
        heartbeatInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        clearTimeout(inactivityWarningTimeout);
        inactivityWarningTimeout = setTimeout(() => {
          playAlertSound();
        }, 4 * 60 * 1000);
      };

      ws.onmessage = (event) => {
        if (destroyed) return;
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'pong': break;

          case 'all_status': {
            console.log('[WS] all_status recibido - usuarios:', data.users?.length || 0);
            console.log('[WS] Usuario actual rol:', user?.rol);

            const asesoresActualizados = (data.users || [])
              .filter(u => {
                // 🔹 Filtrar: solo procesar usuarios de la sede del líder
                const userId = u.userId || u.id_asesor || u.id;
                const pertenece = asesorPerteneceASede(userId);

                if (!pertenece) {
                  console.log('[WS] Filtrando usuario que no pertenece a la sede:', {
                    userId,
                    nombre: u.nombre
                  });
                }

                return pertenece;
              })
              .map(u => {
                const estadoNormalizado = extractEstadoSlug(
                  u.estado || u.estado_actual || u.estado_slug || u.estado_obj
                );
                const inicioEstado = u.inicio_estado || u.inicio || null;
                let tiempoEnEstado = 0;
                if (inicioEstado) {
                  if (u.tiempo_en_estado_segundos || u.tiempo_en_estado || u.tiempo) {
                    tiempoEnEstado = u.tiempo_en_estado_segundos || u.tiempo_en_estado || u.tiempo;
                  } else {
                    const inicioMs = new Date(inicioEstado).getTime();
                    tiempoEnEstado = Math.floor((Date.now() - inicioMs) / 1000);
                  }
                }
                return {
                  ...u,
                  estado: estadoNormalizado,
                  inicio_estado: inicioEstado,
                  tiempo_en_estado: tiempoEnEstado,
                  lastUpdate: new Date().toISOString(),
                };
              });

            console.log('[WS] Usuarios después de filtrar:', asesoresActualizados.length);

            setAsesoresConectados(prev => {
              return asesoresActualizados.map(nuevoAsesor => {
                const asesorExistente = prev.find(a => a.userId === nuevoAsesor.userId);
                if (asesorExistente &&
                    asesorExistente.estado === nuevoAsesor.estado &&
                    asesorExistente.inicio_estado) {
                  const tiempoActualizado = Math.floor(
                    (Date.now() - new Date(asesorExistente.inicio_estado).getTime()) / 1000
                  );
                  return {
                    ...nuevoAsesor,
                    inicio_estado: asesorExistente.inicio_estado,
                    tiempo_en_estado: tiempoActualizado,
                    lastUpdate: new Date().toISOString(),
                  };
                }
                return nuevoAsesor;
              });
            });
            break;
          }

          case 'estado_cambio': {
            // 🔹 Filtrar: solo actualizar si pertenece a la sede del líder
            if (asesorPerteneceASede(data.userId)) {
              actualizarEstadoAsesor({
                userId: data.userId,
                nombre: data.nombre,
                cargo: data.cargo ?? null,
                area: data.area ?? null,
                estado: data.estado,
                inicio_estado: data.inicio_estado || data.inicio,
                tiempo_en_estado: data.tiempo_en_estado || data.tiempo
              });
            } else {
              console.log('[WS] Ignorando estado_cambio de usuario que no pertenece a la sede:', {
                userId: data.userId,
                nombre: data.nombre
              });
            }
            break;
          }

          case 'user_connected':
            // 🔹 Filtrar: solo agregar si pertenece a la sede del líder
            if (asesorPerteneceASede(data.userId)) {
              agregarAsesorConectado(data);
            } else {
              console.log('[WS] Ignorando user_connected de usuario que no pertenece a la sede:', {
                userId: data.userId,
                nombre: data.nombre
              });
            }
            break;

          case 'user_disconnected':
            // 🔹 Filtrar: solo remover si pertenece a la sede del líder
            if (asesorPerteneceASede(data.userId)) {
              removerAsesorConectado(data.userId);
            } else {
              console.log('[WS] Ignorando user_disconnected de usuario que no pertenece a la sede:', {
                userId: data.userId
              });
            }
            break;

          default: break;
        }
      };

      ws.onclose = (evt) => {
        if (destroyed) return;
        setWsConectado(false);
        clearTimers();
        if (evt.code !== 1000) {
          console.warn('[WS] Conexión cerrada, reintentando...', evt.code);
        }
        scheduleReconnect();
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
      };
    };

    connectWS();

    return () => {
      destroyed = true;
      setWsConectado(false);
      clearTimers();
      try { ws?.close(); } catch {}
      wsRef.current = null;
    };
  }, [user?.id]);

  async function cargarAsesoresPorSede(id_sedes) {
    try {
      setCargandoAsesores(true);
      setErrorAsesores(null);

      console.log('[cargarAsesoresPorSede] Usuario:', user);
      console.log('[cargarAsesoresPorSede] ¿Es Lider talento humano?', user?.rol === 'Lider talento humano');

      // 🔹 Si es Lider talento humano, NO llamar a la API de sede
      // Verán todos los asesores conectados vía WebSocket
      if (user?.rol === 'Lider talento humano') {
        console.log('[cargarAsesoresPorSede] Es Lider talento humano - saltando carga de asesores por sede');
        setAsesores([]); // No necesitamos filtrar por sede
        setCargandoAsesores(false);
        return;
      }

      // Preparar payload según el formato esperado por la API
      // id_sedes debe ser un array
      const payload = {
        id_sedes: Array.isArray(id_sedes) ? id_sedes : [id_sedes],
        es_socio: Boolean(user?.es_socio),
        es_lider: Boolean(user?.es_lider),
      };

      // Incluir cargo_id si está disponible
      if (user?.cargo_id) {
        payload.cargo_id = user.cargo_id;
      }

      console.log('[cargarAsesoresPorSede] Payload a enviar:', payload);

      const resp = await workforceApi.getAsesoresPorSede(payload);
      console.log('[cargarAsesoresPorSede] Respuesta del backend:', resp);
      const lista = Array.isArray(resp) ? resp : (resp?.asesores || []);
      console.log('[cargarAsesoresPorSede] Lista extraída:', lista);
      const normalized = lista.map(normalizarAsesor);
      console.log('[cargarAsesoresPorSede] Asesores normalizados:', normalized);
      setAsesores(normalized);

      // Extraer sedes únicas de los asesores
      const sedesUnicas = [];
      const sedesMap = new Map();
      normalized.forEach(asesor => {
        if (asesor.id_sede && !sedesMap.has(asesor.id_sede)) {
          sedesMap.set(asesor.id_sede, {
            id_sede: asesor.id_sede,
            nombre_sede: asesor.nombre_sede || `Sede ${asesor.id_sede}`
          });
        }
      });
      setSedesDisponibles(Array.from(sedesMap.values()).sort((a, b) =>
        (a.nombre_sede || '').localeCompare(b.nombre_sede || '')
      ));
    } catch (err) {
      console.error('[cargarAsesoresPorSede] Error:', err);
      setErrorAsesores('No se pudieron cargar los asesores por sede');
      setAsesores([]);
    } finally {
      setCargandoAsesores(false);
    }
  }

  const actualizarEstadoAsesor = (incoming) => {
    setAsesoresConectados(prev => {
      const idx = prev.findIndex(a => a.userId === incoming.userId);
      const estadoReal = incoming.estado;
      const inicioReal = incoming.inicio_estado;
      const tiempoReal = incoming.tiempo_en_estado;

      if (idx !== -1) {
        return prev.map((a, i) =>
          i === idx
            ? {
                ...a,
                estado: estadoReal,
                inicio_estado: inicioReal,
                tiempo_en_estado: tiempoReal,
                lastUpdate: incoming.timestamp || new Date().toISOString()
              }
            : a
        );
      }
      return [
        ...prev,
        {
          userId: incoming.userId,
          nombre: incoming.nombre,
          cargo: incoming.cargo,
          area: incoming.area,
          estado: estadoReal,
          inicio_estado: inicioReal,
          tiempo_en_estado: tiempoReal,
          lastUpdate: incoming.timestamp || new Date().toISOString()
        }
      ];
    });
  };

  const agregarAsesorConectado = (data) => {
    setAsesoresConectados(prev => {
      if (!prev.find(a => a.userId === data.userId)) {
        return [...prev, {
          userId: data.userId,
          nombre: data.nombre,
          cargo: data.cargo,
          area: data.area,
          estado: data.estado || 'desconectado',
          lastUpdate: new Date().toISOString()
        }];
      }
      return prev;
    });
  };

  const removerAsesorConectado = (userId) => {
    setAsesoresConectados(prev => prev.filter(a => a.userId !== userId));
  };

  const cargarJornadaAsesor = async (idAsesor, fecha = null) => {
    try {
      const params = fecha ? { date: fecha } : {};
      const jornada = await workforceApi.getAsesorJornada(idAsesor, params);
      setJornadasAsesores(prev => ({ ...prev, [idAsesor]: jornada }));
    } catch (err) {
      console.error(`[Jornada] Error al cargar jornada del asesor ${idAsesor}:`, err);
    }
  };

  async function cargarEstadosAsesor(idAsesor) {
    try {
      const estados = await workforceApi.getAsesorEstados(idAsesor);
      setEstadosDisponibles(prev => ({ ...prev, [idAsesor]: estados || [] }));
    } catch (err) {
      setFallosEstados(prev => {
        if (!prev.has(idAsesor)) {
          console.error(`[Estados] ${idAsesor}:`, err.userMessage || err.message);
          const copy = new Set(prev);
          copy.add(idAsesor);
          return copy;
        }
        return prev;
      });
      setEstadosDisponibles(prev => ({ ...prev, [idAsesor]: [] }));
    }
  }

  const cargarHorarioActualAsesor = async (idAsesor) => {
    try {
      const horario = await workforceApi.getHorarioActualAsesor(idAsesor);
      setHorariosActuales(prev => ({ ...prev, [idAsesor]: horario }));
    } catch (err) {
      console.error(`[HorarioActual] Error al cargar horario del asesor ${idAsesor}:`, err);
    }
  };

  useEffect(() => {
    if (asesoresConectados.length > 0 && activeTab === 'control') {
      asesoresConectados.forEach(asesor => {
        const idAsesor = asesor.userId || asesor.id_asesor;
        if (idAsesor) {
          cargarJornadaAsesor(idAsesor);
          cargarEstadosAsesor(idAsesor);
          cargarHorarioActualAsesor(idAsesor);
          // ← ya NO necesitamos cargarStatusAsesor aquí,
          //   TiempoEnEstado lo hace internamente si inicio_estado es null
        }
      });
    }
  }, [asesoresConectados.length, activeTab]);

  const getEstadoInfo = (estado, idAsesor) => {
    const normalized = String(estado || '').toLowerCase().trim();
    const listaAsesor = estadosDisponibles[idAsesor] || [];
    const fromAsesor = listaAsesor.find(e => {
      const codigo = (e.codigo || e.slug || '').toString().toLowerCase().trim();
      return codigo === normalized || (e.nombre && toSlug(e.nombre) === normalized);
    });
    if (fromAsesor) {
      return { color: fromAsesor.color_hex || '#6b7280', label: fromAsesor.nombre || estado || 'Sin estado' };
    }
    const fromCat = estadosCatalogoIndex[normalized] ||
      Object.values(estadosCatalogoIndex).find(e => e.nombre && toSlug(e.nombre) === normalized);
    if (fromCat) {
      return { color: fromCat.color_hex || '#6b7280', label: fromCat.nombre || estado || 'Sin estado' };
    }
    if (!normalized) return { color: '#6b7280', label: 'Desconectado' };
    const niceLabel = estado
      ? String(estado).charAt(0).toUpperCase() + String(estado).slice(1)
      : 'Sin estado';
    return { color: '#6b7280', label: niceLabel };
  };

  const getEstadoColor = (estado, idAsesor) => getEstadoInfo(estado, idAsesor).color;
  const getEstadoTexto = (estado, idAsesor) => getEstadoInfo(estado, idAsesor).label;

  const estadosFiltroOptions = useMemo(() => {
    const opciones = [];
    const seen = new Set();
    if (estadoTipos && estadoTipos.length > 0) {
      estadoTipos.forEach((e) => {
        const codigo = (e.slug || e.codigo || e.nombre || '').toString().toLowerCase();
        if (!codigo || seen.has(codigo)) return;
        seen.add(codigo);
        opciones.push({ value: codigo, label: e.nombre || codigo });
      });
      return opciones;
    }
    asesoresConectados.forEach((a) => {
      const codigo = (a.estado || '').toString().toLowerCase();
      if (!codigo || seen.has(codigo)) return;
      seen.add(codigo);
      const info = getEstadoInfo(codigo);
      opciones.push({ value: codigo, label: info.label });
    });
    return opciones;
  }, [estadoTipos, asesoresConectados]);

  const renderJornada = (idAsesor) => {
    const jornada = jornadasAsesores[idAsesor];
    if (!jornada) return null;
    return (
      <div style={styles.jornadaInfo}>
        {jornada.breaks != null && (
          <div style={styles.jornadaItem}>
            <Activity size={12} />
            <span>{jornada.breaks} breaks</span>
          </div>
        )}
      </div>
    );
  };

  const renderPuntualidad = (idAsesor) => {
    const jornada = jornadasAsesores[idAsesor];
    const horario = horariosActuales[idAsesor];
    if (!jornada || !horario) return null;
    const entradaRealStr = jornada.hora_entrada_real || jornada.hora_entrada || jornada.entrada;
    const entradaProgStr = horario.hora_entrada;
    const fechaBase = jornada.fecha || horario.fecha || horario.fecha_inicio || new Date().toISOString().split('T')[0];
    if (!entradaRealStr || !entradaProgStr) return null;
    const entradaReal = new Date(entradaRealStr);
    const entradaProg = new Date(`${fechaBase}T${entradaProgStr}`);
    const diffMs = entradaReal.getTime() - entradaProg.getTime();
    const diffMin = Math.round(diffMs / 60000);
    let label = 'A tiempo';
    let color = '#10b981';
    if (diffMin > 2) { label = `Tarde (${diffMin} min)`; color = '#ef4444'; }
    else if (diffMin < -2) { label = `Temprano (${Math.abs(diffMin)} min)`; color = '#3b82f6'; }
    return (
      <div
        style={{ ...estiloPuntualidad, background: `${color}15`, color }}
        title={`Entrada prog: ${entradaProgStr} • Real: ${entradaReal.toLocaleTimeString('es-CO')}`}
      >
        <Clock size={12} />
        <span style={{ marginLeft: 4 }}>{label}</span>
      </div>
    );
  };

  const cargarHorarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workforceApi.getHorarios({ lider_id: user.id });
      const lista = Array.isArray(data) ? data : (data?.results || []);
      setHorariosCreados(lista);
    } catch (err) {
      console.error('[Horarios] Error:', err);
      setError('No se pudieron cargar los horarios');
      setHorariosCreados([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarSolicitudes = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await workforceApi.getSolicitudes({ estado: 'pendiente' });
      const lista = Array.isArray(resp) ? resp : (resp?.results || resp?.solicitudes || []);
      setSolicitudes(lista);
    } catch (err) {
      console.error('[Solicitudes] Error:', err);
      setError(err.message || 'No se pudieron cargar las solicitudes');
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignarHorario = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const asesorObj = asesores.find(a => String(a.id_asesor) === String(asesorSeleccionado));
      if (!asesorObj) throw new Error('Debes seleccionar un asesor válido');
      const horarioData = {
        lider_id: user.id,
        asesor_id: asesorObj.id_asesor,
        asesor_nombre: asesorObj.nombre,
        asesor_cargo: asesorObj.cargo || '',
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin || null,
        hora_entrada: horaEntrada,
        hora_salida: horaSalida,
        dias_semana: Object.entries(diasSemana).filter(([_, activo]) => activo).map(([dia]) => dia),
        minutos_adicionales: minutosAdicionales || 0,
        motivo: motivo || null,
      };
      if (horarioEditando) {
        await workforceApi.actualizarHorario(horarioEditando.id, horarioData);
        notify('Horario actualizado correctamente', 'success');
      } else {
        await workforceApi.crearHorario(horarioData);
        notify('Horario asignado correctamente', 'success');
      }
      limpiarFormulario();
      await cargarHorarios();
    } catch (err) {
      console.error('[Horario] Error:', err);
      setError(err.message || 'Error al asignar horario');
      notify('Error al asignar horario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarHorario = (horario) => {
    setHorarioEditando(horario);
    setAsesorSeleccionado(horario.asesor_id);
    setFechaInicio(horario.fecha_inicio);
    setFechaFin(horario.fecha_fin || '');
    setHoraEntrada(horario.hora_entrada);
    setHoraSalida(horario.hora_salida);
    setMinutosAdicionales(horario.minutos_adicionales || 0);
    setMotivo(horario.motivo || '');
    const nuevoDiasSemana = {
      lunes: false, martes: false, miercoles: false, jueves: false,
      viernes: false, sabado: false, domingo: false
    };
    if (horario.dias_semana && Array.isArray(horario.dias_semana)) {
      horario.dias_semana.forEach(dia => {
        if (nuevoDiasSemana.hasOwnProperty(dia)) nuevoDiasSemana[dia] = true;
      });
    }
    setDiasSemana(nuevoDiasSemana);
    setActiveTab('asignar');
  };

  const handleEliminarHorario = async (id) => {
    if (!confirm('¿Estás seguro de eliminar este horario?')) return;
    try {
      setLoading(true);
      setError(null);
      await workforceApi.eliminarHorario(id);
      notify('Horario eliminado', 'success');
      await cargarHorarios();
    } catch (err) {
      console.error('[Eliminar] Error:', err);
      setError(err.message || 'Error al eliminar horario');
      notify('Error al eliminar horario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const limpiarFormulario = () => {
    setHorarioEditando(null);
    setSedeSeleccionada('');
    setAsesorSeleccionado('');
    setBusquedaSede('');
    setBusquedaAsesor('');
    setFechaInicio('');
    setFechaFin('');
    setHoraEntrada('09:00');
    setHoraSalida('18:00');
    setMinutosAdicionales(0);
    setMotivo('');
    setDiasSemana({
      lunes: true, martes: true, miercoles: true, jueves: true,
      viernes: true, sabado: false, domingo: false
    });
  };

  const handleAprobarSolicitud = async (id) => {
    if (!confirm('¿Aprobar esta solicitud?')) return;
    try {
      setLoading(true);
      setError(null);
      await workforceApi.aprobarSolicitud(id, {
        aprobado_por: user.id,
        fecha_aprobacion: new Date().toISOString()
      });
      notify('Solicitud aprobada', 'success');
      await cargarSolicitudes();
    } catch (err) {
      console.error('[Aprobar] Error:', err);
      setError(err.message || 'Error al aprobar solicitud');
      notify('Error al aprobar solicitud', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRechazarSolicitud = async (id) => {
    const razon = prompt('Motivo del rechazo:');
    if (!razon) return;
    try {
      setLoading(true);
      setError(null);
      await workforceApi.rechazarSolicitud(id, {
        rechazado_por: user.id,
        motivo_rechazo: razon,
        fecha_rechazo: new Date().toISOString()
      });
      notify('Solicitud rechazada', 'success');
      await cargarSolicitudes();
    } catch (err) {
      console.error('[Rechazar] Error:', err);
      setError(err.message || 'Error al rechazar solicitud');
      notify('Error al rechazar solicitud', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    try {
      setLoading(true);
      setError(null);
      const blob = await workforceApi.exportarAsesores({
        area: filtroArea !== 'todas' ? filtroArea : undefined,
        estado: filtroEstado !== 'todos' ? filtroEstado : undefined
      });
      workforceApi.downloadFile(blob, `asesores_${new Date().toISOString().split('T')[0]}.xlsx`);
      notify('Archivo exportado correctamente', 'success');
    } catch (err) {
      console.error('[Exportar] Error:', err);
      setError(err.message || 'Error al exportar');
      notify('Error al exportar asesores', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDia = (dia) => {
    setDiasSemana(prev => ({ ...prev, [dia]: !prev[dia] }));
  };

  // 🔹 Set de IDs que pertenecen a la sede del líder (construido desde la lista de la API)
  const idsDeSedeSet = useMemo(() => {
    const set = new Set();
    asesores.forEach(a => {
      const id = a.id_asesor;
      if (id != null) {
        set.add(id);           // original (number o string)
        set.add(String(id));   // como string
        set.add(Number(id));   // como number
      }
    });
    return set;
  }, [asesores]);

  // Filtrar asesores por sede seleccionada en el formulario
  const asesoresPorSede = useMemo(() => {
    if (!sedeSeleccionada) return asesores;
    return asesores.filter(a => String(a.id_sede) === String(sedeSeleccionada));
  }, [asesores, sedeSeleccionada]);

  // Filtrar sedes por búsqueda
  const sedesFiltradas = useMemo(() => {
    if (!busquedaSede) return sedesDisponibles;
    const term = busquedaSede.toLowerCase();
    return sedesDisponibles.filter(s =>
      (s.nombre_sede || '').toLowerCase().includes(term)
    );
  }, [sedesDisponibles, busquedaSede]);

  // Filtrar asesores por búsqueda
  const asesoresFiltradosForm = useMemo(() => {
    if (!busquedaAsesor) return asesoresPorSede;
    const term = busquedaAsesor.toLowerCase();
    return asesoresPorSede.filter(a =>
      (a.nombre || '').toLowerCase().includes(term) ||
      (a.cargo || '').toLowerCase().includes(term) ||
      (a.nombre_sede || '').toLowerCase().includes(term)
    );
  }, [asesoresPorSede, busquedaAsesor]);

  const asesoresFiltrados = asesoresConectados.filter(a => {
    const idAsesor = a.userId || a.id_asesor;

    // 🔹 Si el rol es "Lider talento humano", puede ver TODO sin filtros de sede
    const esLiderTalentoHumano = user?.rol === 'Lider talento humano';

    // Solo mostrar si pertenece a la sede del líder (excepto para Lider talento humano)
    // Si la lista de asesores aún no cargó, mostramos todos para no ocultar nada por error
    const matchSede = esLiderTalentoHumano ||
                  user?.cargo_id === 22 ||  
                  (cargandoAsesores ? false : (
                    idsDeSedeSet.has(idAsesor) ||
                    idsDeSedeSet.has(String(idAsesor)) ||
                    idsDeSedeSet.has(Number(idAsesor))
                  ));

    // Debug de filtrado (solo para roles que no son Lider talento humano)
    if (!matchSede && !cargandoAsesores && !esLiderTalentoHumano) {
      console.log('[Filtrado] Asesor NO coincide con sede:', {
        nombre: a.nombre,
        idAsesor,
        idsDeSedeSet: Array.from(idsDeSedeSet)
      });
    }

    const matchArea = filtroArea === 'todas' || a.area === filtroArea;
    
    const idAsesorStr = String(a.userId || a.id_asesor || '');
    const term = busqueda.toLowerCase().trim();
    const matchBusqueda = !term ||
      (a.nombre?.toLowerCase().includes(term)) ||
      idAsesorStr.includes(term);



    const estadoNormalizado = (a.estado || '').toString().toLowerCase();
    const matchEstado = filtroEstado === 'todos' || estadoNormalizado === filtroEstado;

    return matchSede && matchArea && matchBusqueda && matchEstado;
  });

  const resumenEstados = useMemo(() => {
    const acc = {};
    // 🔹 Si el rol es "Lider talento humano", contar TODOS los asesores
    const esLiderTalentoHumano = user?.rol === 'Lider talento humano';

    asesoresConectados
      .filter(a => {
        // Si es Lider talento humano, no filtrar por sede
        if (esLiderTalentoHumano) return true;
        if (user?.cargo_id === 22) return true; 

        const idAsesor = a.userId || a.id_asesor;
        return asesores.length === 0 || idsDeSedeSet.has(idAsesor) || idsDeSedeSet.has(String(idAsesor));
      })
      .forEach((a) => {
        const key = (a.estado || 'desconectado').toString().toLowerCase();
        acc[key] = (acc[key] || 0) + 1;
      });
    return acc;
  }, [asesoresConectados, idsDeSedeSet, asesores.length, user?.rol]);

  const formatearDias = (dias) => {
    if (!dias || !Array.isArray(dias)) return '-';
    const diasAbrev = {
      lunes: 'L', martes: 'M', miercoles: 'X', jueves: 'J',
      viernes: 'V', sabado: 'S', domingo: 'D'
    };
    return dias.map(d => diasAbrev[d] || d.charAt(0).toUpperCase()).join(', ');
  };

  // Horarios filtrados y ordenados
  const horariosFiltrados = useMemo(() => {
    let filtrados = [...horariosCreados];

    // Filtrar por nombre de asesor
    if (filtroHorarioAsesor) {
      const busqueda = filtroHorarioAsesor.toLowerCase();
      filtrados = filtrados.filter(h =>
        (h.asesor_nombre || '').toLowerCase().includes(busqueda)
      );
    }

    // Filtrar por fecha
    if (filtroHorarioFecha) {
      filtrados = filtrados.filter(h => {
        const fechaInicio = h.fecha_inicio || '';
        const fechaFin = h.fecha_fin || '';
        return fechaInicio.startsWith(filtroHorarioFecha) || fechaFin.startsWith(filtroHorarioFecha);
      });
    }

    // Ordenar
    if (filtroHorarioOrden === 'recientes') {
      filtrados.sort((a, b) => new Date(b.fecha_inicio) - new Date(a.fecha_inicio));
    } else if (filtroHorarioOrden === 'antiguos') {
      filtrados.sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio));
    } else if (filtroHorarioOrden === 'nombre') {
      filtrados.sort((a, b) => (a.asesor_nombre || '').localeCompare(b.asesor_nombre || ''));
    }

    return filtrados;
  }, [horariosCreados, filtroHorarioAsesor, filtroHorarioFecha, filtroHorarioOrden]);

  // Paginación
  const totalPaginas = Math.ceil(horariosFiltrados.length / horariosPorPagina);
  const indiceInicio = (paginaActual - 1) * horariosPorPagina;
  const horariosPaginados = horariosFiltrados.slice(indiceInicio, indiceInicio + horariosPorPagina);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1);
  }, [filtroHorarioAsesor, filtroHorarioFecha, filtroHorarioOrden]);


  const fmtFecha = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('es-CO'); } catch { return iso; }
  };

  const badgeStylesByEstado = (estado) => {
    const s = String(estado || '').toLowerCase();
    if (s === 'aprobado') return { bg: '#10b98120', color: '#10b981', label: 'Aprobado' };
    if (s === 'rechazado') return { bg: '#ef444420', color: '#ef4444', label: 'Rechazado' };
    return { bg: '#f59e0b20', color: '#f59e0b', label: 'Pendiente' };
  };

  const solicitudesPendientes = (solicitudes || []).filter(
    (s) => String(s.estado || '').toLowerCase() === 'pendiente'
  );

  const [estadosCat, setEstadosCat] = useState([]);
  const [estadoEditando, setEstadoEditando] = useState(null);
  const [formEstado, setFormEstado] = useState({
    nombre: '', slug: '', color_hex: '#10b981', limite_minutos_default: 60,
  });
  const [cfgAsesor, setCfgAsesor] = useState({
    asesor: '', estado_id: '', activo: true, limite_minutos: '', color_hex_override: '',
  });
  const [configsAsesor, setConfigsAsesor] = useState([]);

  const [rangoLimite, setRangoLimite] = useState({ min: '', max: '' });
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'id', direccion: 'asc' });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  useEffect(() => {
    if (activeTab === 'estados') cargarEstados();
  }, [activeTab]);

  useEffect(() => {
    if (!cfgAsesor.asesor) { setConfigsAsesor([]); return; }
    cargarConfigsAsesor(cfgAsesor.asesor);
    cargarHorariosDeAsesor(cfgAsesor.asesor);
  }, [cfgAsesor.asesor]);

  useEffect(() => {
    if (activeTab === 'estados' && cfgAsesor.asesor) {
      cargarConfigsAsesor(cfgAsesor.asesor);
    }
  }, [activeTab, cfgAsesor.asesor]);

  async function cargarEstados() {
    try {
      const list = await workforceApi.getEstados();
      setEstadosCat(Array.isArray(list) ? list : (list?.results || []));
    } catch (e) {
      console.error('[Estados] error al cargar', e);
    }
  }

  async function cargarConfigsAsesor(idAsesor) {
    try {
      const list = await workforceApi.getEstadoConfigs({ asesor: idAsesor });
      setConfigsAsesor(Array.isArray(list) ? list : (list?.results || []));
    } catch (e) {
      console.error('[EstadoConfig] error al cargar', e);
      setConfigsAsesor([]);
    }
  }

  async function cargarHorariosDeAsesor(idAsesor) {
    try {
      const data = await workforceApi.getHorariosPorAsesor(idAsesor);
      const lista = Array.isArray(data) ? data : (data?.results || []);
      setHorariosCreados(lista);
    } catch (e) {
      console.error('[Horarios] por asesor:', e);
    }
  }

  const toValidHex = (val) => {
    const raw = (val || '').trim();
    if (!raw) return null;
    const withHash = raw.startsWith('#') ? raw : `#${raw}`;
    const upper = withHash.toUpperCase();
    return /^#[0-9A-F]{6}$/.test(upper) ? upper : 'INVALID';
  };

  const estadosFiltrados = useMemo(() => {
    let resultado = [...estadosCat];
    if (busqueda.trim()) {
      const busquedaLower = busqueda.toLowerCase();
      resultado = resultado.filter(e =>
        e.nombre.toLowerCase().includes(busquedaLower) ||
        (e.slug || '').toLowerCase().includes(busquedaLower) ||
        e.id.toString().includes(busquedaLower) ||
        (e.color_hex || '').toLowerCase().includes(busquedaLower)
      );
    }
    if (rangoLimite.min !== '') {
      resultado = resultado.filter(e => (e.limite_minutos_default ?? 0) >= parseInt(rangoLimite.min));
    }
    if (rangoLimite.max !== '') {
      resultado = resultado.filter(e => (e.limite_minutos_default ?? 0) <= parseInt(rangoLimite.max));
    }
    resultado.sort((a, b) => {
      let valorA = a[ordenamiento.campo];
      let valorB = b[ordenamiento.campo];
      if (valorA === null || valorA === undefined) valorA = '';
      if (valorB === null || valorB === undefined) valorB = '';
      if (typeof valorA === 'string') { valorA = valorA.toLowerCase(); valorB = valorB.toLowerCase(); }
      if (valorA < valorB) return ordenamiento.direccion === 'asc' ? -1 : 1;
      if (valorA > valorB) return ordenamiento.direccion === 'asc' ? 1 : -1;
      return 0;
    });
    return resultado;
  }, [estadosCat, busqueda, rangoLimite, ordenamiento]);

  const limpiarFiltros = () => {
    setBusqueda('');
    setRangoLimite({ min: '', max: '' });
    setOrdenamiento({ campo: 'id', direccion: 'asc' });
  };

  const cambiarOrdenamiento = (campo) => {
    setOrdenamiento(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div style={styles.container}>
      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>
            Panel de Control - {user?.rol === 'Lider talento humano' ? 'Talento Humano' : 'Líder'}
          </h2>
          <p style={styles.subtitle}>{user?.cargo || 'Líder'} - {user?.nombre || 'Usuario'}</p>
          {user?.rol === 'Lider talento humano' && (
            <p style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 500 }}>
              ✓ Acceso total a todas las sedes
            </p>
          )}
        </div>
        <div style={styles.wsStatus}>
          {wsConectado ? (
            <><Wifi size={16} style={{ color: '#10b981' }} /><span>Conectado en tiempo real</span></>
          ) : (
            <><WifiOff size={16} style={{ color: '#ef4444' }} /><span>Reconectando...</span></>
          )}
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'control' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('control')}
        >
          <UserCheck size={18} />
          Control en Tiempo Real ({asesoresFiltrados.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'asignar' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('asignar'); limpiarFormulario(); }}
        >
          <Calendar size={16} />
          {horarioEditando ? 'Editar Horario' : 'Asignar Horarios'}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'horarios' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('horarios')}
        >
          <List size={18} />
          Mis Horarios ({horariosCreados.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'estados' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('estados')}
        >
          <List size={18} />
          Estados
        </button>
      </div>

      {activeTab === 'control' && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Asesores Conectados - Tiempo Real</h3>
              <div style={styles.filters}>
                <div style={styles.searchBox}>
                  <Search size={16} style={styles.searchIcon} />
                  <input
                    type="text"
                    style={styles.searchInput}
                    placeholder="Buscar..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div style={styles.estadisticasGrid}>
              <div style={styles.estadisticaCard}>
                <div style={styles.estadisticaValor}>{asesoresFiltrados.length}</div>
                <div style={styles.estadisticaLabel}>Conectados</div>
              </div>
              {(estadoTipos || []).map((estado) => {
                const codigo = (estado.slug || estado.codigo || estado.nombre || '').toString().toLowerCase();
                if (!codigo) return null;
                const cantidad = resumenEstados[codigo] || 0;
                const info = getEstadoInfo(codigo);
                return (
                  <div key={codigo} style={styles.estadisticaCard}>
                    <div style={styles.estadisticaValor}>{cantidad}</div>
                    <div style={styles.estadisticaLabel}>{info.label}</div>
                  </div>
                );
              })}
              {estadoTipos?.length === 0 &&
                Object.entries(resumenEstados).map(([codigo, cantidad]) => {
                  if (codigo === 'desconectado') return null;
                  const info = getEstadoInfo(codigo);
                  return (
                    <div key={codigo} style={styles.estadisticaCard}>
                      <div style={styles.estadisticaValor}>{cantidad}</div>
                      <div style={styles.estadisticaLabel}>{info.label}</div>
                    </div>
                  );
                })}
            </div>

            {asesoresFiltrados.length === 0 ? (
              <div style={styles.emptyState}>
                <WifiOff size={48} style={{ color: '#6b7280' }} />
                <p>
                  {cargandoAsesores
                    ? 'Cargando asesores...'
                    : !wsConectado
                      ? 'Conectando...'
                      : 'No hay asesores conectados'}
                </p>
                {!cargandoAsesores && asesores.length === 0 && errorAsesores && (
                  <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8 }}>{errorAsesores}</p>
                )}
              </div>
            ) : (
              <div style={styles.asesorGrid}>
                {asesoresFiltrados.map(asesor => {
                  const idAsesor = asesor.userId || asesor.id_asesor;
                  const { color: estadoColor, label: estadoLabel } = getEstadoInfo(asesor.estado, idAsesor);

                  return (
                    <div key={idAsesor} style={styles.asesorCard}>
                      <div style={styles.asesorHeader}>
                        <div style={styles.asesorAvatar}>
                          {twoInitials(asesor.nombre)}
                        </div>
                        <span style={{ ...styles.estadoDot, background: estadoColor }} />
                      </div>

                      <div style={styles.asesorInfo}>
                        <h4 style={styles.asesorNombre}>{asesor.nombre}</h4>
                        <p style={styles.asesorCargo}>{asesor.cargo}</p>
                        <p style={styles.asesorArea}>{asesor.area}</p>
                      </div>

                      <div style={styles.asesorEstado}>
                        <span style={{
                          ...styles.estadoBadge,
                          background: `${estadoColor}20`,
                          color: estadoColor
                        }}>
                          {estadoLabel}
                        </span>

                        {/*
                          ✅ CAMBIO CLAVE:
                          - Pasa idAsesor y estadoActual al componente
                          - Si inicio_estado viene del WS, lo usa directamente
                          - Si es null, TiempoEnEstado llama a /api/asesores/{id}/status/
                            automáticamente y verifica que el estado coincida
                        */}
                        <TiempoEnEstado
                          inicioEstado={asesor.inicio_estado}
                          idAsesor={idAsesor}
                          estadoActual={asesor.estado}
                        />
                      </div>

                      {renderJornada(idAsesor)}
                      {renderPuntualidad(idAsesor)}

                      {Array.isArray(estadosDisponibles[idAsesor]) && estadosDisponibles[idAsesor].length > 0 && (
                        <div style={estiloEstadosList}>
                          {estadosDisponibles[idAsesor].map(est => {
                            const isActive = String(asesor.estado || '').toLowerCase() === String(est.codigo || '').toLowerCase();
                            return (
                              <span
                                key={est.id || est.codigo}
                                style={{
                                  ...estiloChip,
                                  ...(isActive ? estiloChipActivo : {}),
                                  borderColor: est.color_hex || '#e5e7eb',
                                }}
                                title={isActive ? 'Estado actual' : 'Estado disponible'}
                              >
                                {est.nombre}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'asignar' && (
        <div style={styles.content}>
          <div style={styles.grid}>
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={styles.cardTitle}>
                  {horarioEditando ? 'Editar Horario' : 'Nueva Asignación de Horario'}
                </h3>
                {horarioEditando && (
                  <button
                    type="button"
                    style={styles.cancelBtnSmall}
                    onClick={limpiarFormulario}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#dc2626';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#dc2626';
                    }}
                  >
                    <X size={14} />
                    Cancelar
                  </button>
                )}
              </div>
              <form onSubmit={handleAsignarHorario}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Seleccionar Sede</label>
                  <div style={{ position: 'relative' }} ref={sedeDropdownRef}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={16} style={{ position: 'absolute', left: 12, color: '#9ca3af', zIndex: 1 }} />
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          paddingLeft: '40px',
                          cursor: 'text',
                        }}
                        placeholder={sedeSeleccionada
                          ? sedesDisponibles.find(s => String(s.id_sede) === String(sedeSeleccionada))?.nombre_sede || 'Seleccionar sede'
                          : 'Buscar sede...'
                        }
                        value={busquedaSede}
                        onChange={(e) => {
                          setBusquedaSede(e.target.value);
                          setSelectSedeAbierto(true);
                        }}
                        onFocus={() => setSelectSedeAbierto(true)}
                        onClick={() => setSelectSedeAbierto(true)}
                      />
                      {busquedaSede && (
                        <button
                          type="button"
                          onClick={() => {
                            setBusquedaSede('');
                            setSedeSeleccionada('');
                          }}
                          style={{
                            position: 'absolute',
                            right: 12,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#9ca3af',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {selectSedeAbierto && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 4,
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}>
                        <div
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                          onClick={() => {
                            setSedeSeleccionada('');
                            setBusquedaSede('');
                            setSelectSedeAbierto(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          -- Todas las sedes --
                        </div>
                        {sedesFiltradas.map((sede) => (
                          <div
                            key={sede.id_sede}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: String(sede.id_sede) === String(sedeSeleccionada) ? 'var(--accent)' : 'transparent',
                              color: 'var(--text)',
                            }}
                            onClick={() => {
                              setSedeSeleccionada(String(sede.id_sede));
                              setBusquedaSede(sede.nombre_sede);
                              setAsesorSeleccionado('');
                              setSelectSedeAbierto(false);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {sede.nombre_sede}
                          </div>
                        ))}
                        {sedesFiltradas.length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                            No se encontraron sedes
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Seleccionar Asesor *</label>
                  <div style={{ position: 'relative' }} ref={asesorDropdownRef}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={16} style={{ position: 'absolute', left: 12, color: '#9ca3af', zIndex: 1 }} />
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          paddingLeft: '40px',
                          cursor: 'text',
                        }}
                        placeholder={asesorSeleccionado
                          ? asesoresPorSede.find(a => String(a.id_asesor) === String(asesorSeleccionado))?.nombre || 'Seleccionar asesor'
                          : 'Buscar asesor...'
                        }
                        value={busquedaAsesor}
                        onChange={(e) => {
                          setBusquedaAsesor(e.target.value);
                          setSelectAsesorAbierto(true);
                        }}
                        onFocus={() => setSelectAsesorAbierto(true)}
                        onClick={() => setSelectAsesorAbierto(true)}
                        disabled={cargandoAsesores}
                      />
                      {busquedaAsesor && (
                        <button
                          type="button"
                          onClick={() => {
                            setBusquedaAsesor('');
                            setAsesorSeleccionado('');
                          }}
                          style={{
                            position: 'absolute',
                            right: 12,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#9ca3af',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {selectAsesorAbierto && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 4,
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}>
                        {asesoresFiltradosForm.map((a) => (
                          <div
                            key={a.id_asesor}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: String(a.id_asesor) === String(asesorSeleccionado) ? 'var(--accent)' : 'transparent',
                              color: 'var(--text)',
                            }}
                            onClick={() => {
                              setAsesorSeleccionado(String(a.id_asesor));
                              setBusquedaAsesor(`${a.nombre}${a.cargo ? ` - ${a.cargo}` : ''}${a.nombre_sede ? ` - ${a.nombre_sede}` : ''}`);
                              setSelectAsesorAbierto(false);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <div style={{ fontWeight: 500, color: 'var(--text)' }}>{a.nombre}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {a.cargo} {a.nombre_sede ? `• ${a.nombre_sede}` : ''}
                            </div>
                          </div>
                        ))}
                        {asesoresFiltradosForm.length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                            {cargandoAsesores ? 'Cargando asesores...' : 'No se encontraron asesores'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Fecha Inicio *</label>
                    <input type="date" style={styles.input} value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Fecha Fin</label>
                    <input type="date" style={styles.input} value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)} />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hora Entrada *</label>
                    <input type="time" style={styles.input} value={horaEntrada}
                      onChange={(e) => setHoraEntrada(e.target.value)} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hora Salida *</label>
                    <input type="time" style={styles.input} value={horaSalida}
                      onChange={(e) => setHoraSalida(e.target.value)} required />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Días de la Semana</label>
                  <div style={styles.diasGrid}>
                    {Object.entries(diasSemana).map(([dia, activo]) => (
                      <button
                        key={dia}
                        type="button"
                        style={{ ...styles.diaBtn, ...(activo ? styles.diaBtnActive : {}) }}
                        onClick={() => handleToggleDia(dia)}
                      >
                        {dia.charAt(0).toUpperCase() + dia.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Motivo / Observaciones</label>
                  <textarea style={styles.textarea} value={motivo}
                    onChange={(e) => setMotivo(e.target.value)} rows="3" />
                </div>

                <button
                  type="submit"
                  style={{ ...styles.submitBtn, ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : (horarioEditando ? 'Actualizar Horario' : 'Asignar Horario')}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'horarios' && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Horarios Creados por Mí</h3>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500 }}>
                {horariosFiltrados.length} {horariosFiltrados.length === 1 ? 'horario' : 'horarios'}
                {horariosFiltrados.length !== horariosCreados.length && ` (filtrado de ${horariosCreados.length})`}
              </div>
            </div>

            {loading && <div style={styles.loadingState}><p>Cargando horarios...</p></div>}

            {!loading && horariosCreados.length === 0 && (
              <div style={styles.emptyState}>
                <Calendar size={48} style={{ color: '#6b7280' }} />
                <p>No has creado ningún horario aún</p>
                <button style={{ ...styles.submitBtn, width: 'auto', marginTop: 16 }}
                  onClick={() => setActiveTab('asignar')}>
                  Crear Primer Horario
                </button>
              </div>
            )}

            {!loading && horariosCreados.length > 0 && (
              <>
                {/* Filtros */}
                <div style={styles.horariosFilters}>
                  <div style={styles.filtrosRow}>
                    {/* Buscador por asesor */}
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>Buscar Asesor</label>
                      <div style={styles.searchBox}>
                        <Search size={16} style={styles.searchIcon} />
                        <input
                          type="text"
                          style={styles.searchInput}
                          placeholder="Nombre del asesor..."
                          value={filtroHorarioAsesor}
                          onChange={(e) => setFiltroHorarioAsesor(e.target.value)}
                        />
                        {filtroHorarioAsesor && (
                          <button
                            style={styles.clearSearchBtn}
                            onClick={() => setFiltroHorarioAsesor('')}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Filtro por fecha */}
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>Fecha</label>
                      <input
                        type="date"
                        style={styles.filterInput}
                        value={filtroHorarioFecha}
                        onChange={(e) => setFiltroHorarioFecha(e.target.value)}
                      />
                    </div>

                    {/* Ordenamiento */}
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>Ordenar por</label>
                      <select
                        style={styles.filterSelect}
                        value={filtroHorarioOrden}
                        onChange={(e) => setFiltroHorarioOrden(e.target.value)}
                      >
                        <option value="recientes">Más recientes</option>
                        <option value="antiguos">Más antiguos</option>
                        <option value="nombre">Nombre (A-Z)</option>
                      </select>
                    </div>

                    {/* Limpiar filtros */}
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>&nbsp;</label>
                      <button
                        style={styles.btnLimpiarFiltros}
                        onClick={() => {
                          setFiltroHorarioAsesor('');
                          setFiltroHorarioFecha('');
                          setFiltroHorarioOrden('recientes');
                        }}
                        disabled={!filtroHorarioAsesor && !filtroHorarioFecha && filtroHorarioOrden === 'recientes'}
                      >
                        <FilterX size={16} />
                        Limpiar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lista de tarjetas */}
                {horariosPaginados.length === 0 ? (
                  <div style={styles.emptyState}>
                    <Filter size={48} style={{ color: '#6b7280' }} />
                    <p>No se encontraron horarios con los filtros aplicados</p>
                    <button
                      style={{ ...styles.btnLimpiarFiltros, width: 'auto', marginTop: 16 }}
                      onClick={() => {
                        setFiltroHorarioAsesor('');
                        setFiltroHorarioFecha('');
                        setFiltroHorarioOrden('recientes');
                      }}
                    >
                      Limpiar Filtros
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={styles.horariosGrid}>
                      {horariosPaginados.map(horario => (
                        <div key={horario.id} style={styles.horarioCard}>
                          {/* Header de la tarjeta */}
                          <div style={styles.horarioCardHeader}>
                            <div style={styles.asesorCelda}>
                              <div style={styles.asesorAvatarCard}>
                                {horario.asesor_nombre?.split(' ').map(n => n[0]).join('').toUpperCase() || 'A'}
                              </div>
                              <div>
                                <div style={styles.asesorNombreCard}>{horario.asesor_nombre || 'Sin nombre'}</div>
                                <div style={styles.asesorCargoCard}>{horario.asesor_cargo || '-'}</div>
                              </div>
                            </div>
                          </div>

                          {/* Contenido de la tarjeta */}
                          <div style={styles.horarioCardBody}>
                            {/* Período */}
                            <div style={styles.horarioInfoRow}>
                              <Calendar size={14} style={styles.infoIcon} />
                              <div>
                                <div style={styles.infoLabel}>Período</div>
                                <div style={styles.infoValue}>
                                  {horario.fecha_inicio}
                                  {horario.fecha_fin && ` - ${horario.fecha_fin}`}
                                </div>
                              </div>
                            </div>

                            {/* Horario */}
                            <div style={styles.horarioInfoRow}>
                              <Clock size={14} style={styles.infoIcon} />
                              <div>
                                <div style={styles.infoLabel}>Horario</div>
                                <div style={styles.infoValue}>
                                  {horario.hora_entrada} - {horario.hora_salida}
                                </div>
                              </div>
                            </div>

                            {/* Días */}
                            <div style={styles.horarioInfoRow}>
                              <List size={14} style={styles.infoIcon} />
                              <div>
                                <div style={styles.infoLabel}>Días</div>
                                <div style={styles.diasBadgeCard}>{formatearDias(horario.dias_semana)}</div>
                              </div>
                            </div>

                            {/* Minutos extra */}
                            {horario.minutos_adicionales > 0 && (
                              <div style={styles.minutosExtraContainer}>
                                <span style={styles.minutosExtraCard}>+{horario.minutos_adicionales} min</span>
                              </div>
                            )}
                          </div>

                          {/* Acciones */}
                          <div style={styles.horarioCardFooter}>
                            <button
                              style={styles.btnCardEdit}
                              onClick={() => handleEditarHorario(horario)}
                              title="Editar"
                            >
                              <Edit2 size={14} />
                              Editar
                            </button>
                            <button
                              style={styles.btnCardDelete}
                              onClick={() => handleEliminarHorario(horario.id)}
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                              Eliminar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Paginación */}
                    {totalPaginas > 1 && (
                      <div style={styles.pagination}>
                        <button
                          style={{ ...styles.paginationBtn, ...(paginaActual === 1 ? styles.paginationBtnDisabled : {}) }}
                          onClick={() => setPaginaActual(1)}
                          disabled={paginaActual === 1}
                          title="Primera página"
                        >
                          <ChevronsLeft size={16} />
                        </button>
                        <button
                          style={{ ...styles.paginationBtn, ...(paginaActual === 1 ? styles.paginationBtnDisabled : {}) }}
                          onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                          disabled={paginaActual === 1}
                          title="Página anterior"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <span style={styles.paginationInfo}>
                          Página {paginaActual} de {totalPaginas}
                        </span>

                        <button
                          style={{ ...styles.paginationBtn, ...(paginaActual === totalPaginas ? styles.paginationBtnDisabled : {}) }}
                          onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                          disabled={paginaActual === totalPaginas}
                          title="Página siguiente"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button
                          style={{ ...styles.paginationBtn, ...(paginaActual === totalPaginas ? styles.paginationBtnDisabled : {}) }}
                          onClick={() => setPaginaActual(totalPaginas)}
                          disabled={paginaActual === totalPaginas}
                          title="Última página"
                        >
                          <ChevronsRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'estados' && (
        <div style={styles.content}>
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Gestión de Estados</h3>

            <div style={styles.estadosFormContainer}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    style={styles.input}
                    value={formEstado.nombre}
                    onChange={e => setFormEstado(f => ({ ...f, nombre: e.target.value }))}
                    placeholder="Ej: Reunión, Break, Almuerzo..."
                    required
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Color identificador</label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <input type="color" style={styles.colorInput}
                      value={formEstado.color_hex || '#10b981'}
                      onChange={e => setFormEstado(f => ({ ...f, color_hex: e.target.value }))} />
                    <span style={{
                      fontFamily: "'Poppins', sans-serif", fontSize: 13,
                      color: 'var(--text-secondary)', background: 'var(--bg)',
                      padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)'
                    }}>
                      {formEstado.color_hex || '#10b981'}
                    </span>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Límite de tiempo (minutos)</label>
                  <input type="number" style={styles.input}
                    value={formEstado.limite_minutos_default}
                    onChange={e => setFormEstado(f => ({ ...f, limite_minutos_default: parseInt(e.target.value || '0', 10) }))}
                    min="0" step="5" placeholder="60" />
                  <small style={{ fontSize: 12, fontFamily: "'Poppins', sans-serif", color: 'var(--text-secondary)', marginTop: 4 }}>
                    Tiempo máximo permitido para este estado
                  </small>
                </div>
              </div>

              <div style={styles.buttonGroup}>
                {estadoEditando ? (
                  <>
                    <button style={styles.submitBtn} onClick={async () => {
                      const payload = {
                        ...formEstado,
                        slug: estadoEditando?.slug || toSlug(formEstado.nombre),
                        limite_minutos_default: Number(formEstado.limite_minutos_default ?? 0),
                      };
                      try {
                        await notifyPromise(
                          workforceApi.actualizarEstado(estadoEditando.id, payload),
                          { loading: 'Guardando cambios...', success: 'Cambios guardados correctamente', error: 'No se pudo actualizar' }
                        );
                        setEstadoEditando(null);
                        setFormEstado({ nombre: '', color_hex: '#10b981', limite_minutos_default: 60 });
                        await cargarEstados();
                      } catch (e) {
                        notify(getErrorMessage(e, 'Error al actualizar'), 'error', { duration: 6000 });
                        console.error(e);
                      }
                    }}>
                      Guardar cambios
                    </button>
                    <button style={styles.cancelBtn} onClick={() => {
                      setEstadoEditando(null);
                      setFormEstado({ nombre: '', color_hex: '#10b981', limite_minutos_default: 60 });
                    }}>
                      ✖️ Cancelar
                    </button>
                  </>
                ) : (
                  <button style={styles.submitBtn} onClick={async () => {
                    try {
                      const payload = {
                        ...formEstado,
                        slug: toSlug(formEstado.nombre),
                        limite_minutos_default: Number(formEstado.limite_minutos_default ?? 0),
                      };
                      await notifyPromise(
                        workforceApi.crearEstado(payload),
                        { loading: 'Creando estado...', success: 'Estado creado exitosamente', error: 'No se pudo crear el estado' }
                      );
                      setFormEstado({ nombre: '', color_hex: '#10b981', limite_minutos_default: 60 });
                      await cargarEstados();
                    } catch (e) {
                      notify(getErrorMessage?.(e, 'Error al crear') || 'Error al crear', 'error', { duration: 6000 });
                      console.error(e);
                    }
                  }}>
                    Crear nuevo estado
                  </button>
                )}
              </div>
            </div>

            <div style={styles.container}>
              <h4 style={styles.header}>Estados registrados</h4>
              <div style={styles.filtrosContainer}>
                <div style={{ ...styles.filtrosHeader, ...(mostrarFiltros ? { marginBottom: 16 } : {}) }}>
                  <button style={styles.btnToggleFiltros} onClick={() => setMostrarFiltros(!mostrarFiltros)}>
                    <SlidersHorizontal size={16} />
                    {mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
                  </button>
                  {(busqueda || rangoLimite.min || rangoLimite.max) && (
                    <button style={styles.btnLimpiar} onClick={limpiarFiltros}>Limpiar filtros</button>
                  )}
                </div>

                <div style={{ ...styles.searchContainer, ...(mostrarFiltros ? { marginBottom: 16 } : {}) }}>
                  <Search size={18} style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, ID, slug o color..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={styles.searchInput}
                  />
                  {busqueda && (
                    <button style={styles.clearBtn} onClick={() => setBusqueda('')}>
                      <X size={18} />
                    </button>
                  )}
                </div>

                {mostrarFiltros && (
                  <div style={styles.filtrosGrid}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Límite mínimo (min)</label>
                      <input type="number" placeholder="Ej: 30" value={rangoLimite.min}
                        onChange={(e) => setRangoLimite(prev => ({ ...prev, min: e.target.value }))}
                        style={styles.input} />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Límite máximo (min)</label>
                      <input type="number" placeholder="Ej: 120" value={rangoLimite.max}
                        onChange={(e) => setRangoLimite(prev => ({ ...prev, max: e.target.value }))}
                        style={styles.input} />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Ordenar por</label>
                      <select value={ordenamiento.campo}
                        onChange={(e) => setOrdenamiento(prev => ({ ...prev, campo: e.target.value }))}
                        style={styles.selectInput}>
                        <option value="id">ID</option>
                        <option value="nombre">Nombre</option>
                        <option value="limite_minutos_default">Límite</option>
                      </select>
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Dirección</label>
                      <select value={ordenamiento.direccion}
                        onChange={(e) => setOrdenamiento(prev => ({ ...prev, direccion: e.target.value }))}
                        style={styles.selectInput}>
                        <option value="asc">Ascendente</option>
                        <option value="desc">Descendente</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.statsBar}>
                <span>Mostrando <strong>{estadosFiltrados.length}</strong> de <strong>{estadosCat.length}</strong> estados</span>
                {estadosFiltrados.length > 0 && (
                  <span>
                    Límite promedio: <strong>
                      {Math.round(estadosFiltrados.reduce((sum, e) => sum + (e.limite_minutos_default ?? 0), 0) / estadosFiltrados.length)}
                    </strong> min
                  </span>
                )}
              </div>

              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th} onClick={() => cambiarOrdenamiento('id')}>
                        ID {ordenamiento.campo === 'id' && <span style={styles.sortIndicator}>{ordenamiento.direccion === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      <th style={styles.th} onClick={() => cambiarOrdenamiento('nombre')}>
                        Nombre del estado {ordenamiento.campo === 'nombre' && <span style={styles.sortIndicator}>{ordenamiento.direccion === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      <th style={styles.th}>Color</th>
                      <th style={styles.th} onClick={() => cambiarOrdenamiento('limite_minutos_default')}>
                        Límite (min) {ordenamiento.campo === 'limite_minutos_default' && <span style={styles.sortIndicator}>{ordenamiento.direccion === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      <th style={styles.th}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estadosFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ padding: 0 }}>
                          <div style={styles.emptyState}>
                            <div style={styles.emptyStateIcon}>
                              {busqueda || rangoLimite.min || rangoLimite.max ? '🔍' : '📭'}
                            </div>
                            <p style={styles.emptyStateText}>
                              {busqueda || rangoLimite.min || rangoLimite.max ? 'No se encontraron resultados' : 'No hay estados registrados'}
                            </p>
                            <small style={{ color: 'var(--text-secondary)' }}>
                              {busqueda || rangoLimite.min || rangoLimite.max
                                ? 'Intenta ajustar los filtros de búsqueda'
                                : 'Crea tu primer estado usando el formulario de arriba'}
                            </small>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      estadosFiltrados.map(e => (
                        <tr key={e.id} style={styles.tr}>
                          <td style={styles.td}>
                            <span style={{
                              fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 600,
                              color: 'var(--text-secondary)', background: 'var(--bg)', padding: '4px 8px', borderRadius: 6
                            }}>#{e.id}</span>
                          </td>
                          <td style={styles.td}><span style={{ fontWeight: 600 }}>{e.nombre}</span></td>
                          <td style={styles.td}>
                            <div style={styles.colorPreview}>
                              <span style={{ ...styles.colorSwatch, background: e.color_hex || '#6b7280' }} />
                              <span style={styles.colorHex}>{e.color_hex}</span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              background: '#dbeafe', color: '#1e40af', padding: '4px 12px',
                              borderRadius: 12, fontSize: 13, fontFamily: "'Poppins', sans-serif", fontWeight: 600
                            }}>
                              {e.limite_minutos_default ?? '-'} min
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.acciones}>
                              <button
                                style={styles.btnEdit}
                                onClick={() => {
                                  setEstadoEditando(e);
                                  setFormEstado({
                                    nombre: e.nombre || '', slug: e.slug || '',
                                    color_hex: e.color_hex || '#10b981',
                                    limite_minutos_default: e.limite_minutos_default ?? 60,
                                  });
                                }}
                                title="Editar estado"
                                onMouseOver={(e) => e.currentTarget.style.background = '#bfdbfe'}
                                onMouseOut={(e) => e.currentTarget.style.background = '#dbeafe'}
                              >
                                <Edit2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.sectionDivider} />

            <h3 style={styles.sectionTitle}>Configuración Personalizada por Asesor</h3>

            <div style={styles.configCard}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Seleccionar Sede</label>
                  <div style={{ position: 'relative' }} ref={cfgSedeDropdownRef}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={16} style={{ position: 'absolute', left: 12, color: '#9ca3af', zIndex: 1 }} />
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          paddingLeft: '40px',
                          cursor: 'text',
                        }}
                        placeholder={cfgSedeSeleccionada
                          ? cfgSedesFiltradas.find(s => String(s.id_sede) === String(cfgSedeSeleccionada))?.nombre_sede || 'Seleccionar sede'
                          : 'Buscar sede...'
                        }
                        value={cfgBusquedaSede}
                        onChange={(e) => {
                          setCfgBusquedaSede(e.target.value);
                          setCfgSelectSedeAbierto(true);
                        }}
                        onFocus={() => setCfgSelectSedeAbierto(true)}
                        onClick={() => setCfgSelectSedeAbierto(true)}
                      />
                      {cfgBusquedaSede && (
                        <button
                          type="button"
                          onClick={() => {
                            setCfgBusquedaSede('');
                            setCfgSedeSeleccionada('');
                          }}
                          style={{
                            position: 'absolute',
                            right: 12,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#9ca3af',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {cfgSelectSedeAbierto && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 4,
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}>
                        <div
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                          onClick={() => {
                            setCfgSedeSeleccionada('');
                            setCfgBusquedaSede('');
                            setCfgSelectSedeAbierto(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          -- Todas las sedes --
                        </div>
                        {cfgSedesFiltradas.map((sede) => (
                          <div
                            key={sede.id_sede}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: String(sede.id_sede) === String(cfgSedeSeleccionada) ? 'var(--accent)' : 'transparent',
                              color: 'var(--text)',
                            }}
                            onClick={() => {
                              setCfgSedeSeleccionada(String(sede.id_sede));
                              setCfgBusquedaSede(sede.nombre_sede);
                              setCfgAsesor(c => ({ ...c, asesor: '' }));
                              setCfgBusquedaAsesor('');
                              setCfgSelectSedeAbierto(false);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {sede.nombre_sede}
                          </div>
                        ))}
                        {cfgSedesFiltradas.length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                            No se encontraron sedes
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Seleccionar Asesor <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={{ position: 'relative' }} ref={cfgAsesorDropdownRef}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={16} style={{ position: 'absolute', left: 12, color: '#9ca3af', zIndex: 1 }} />
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          paddingLeft: '40px',
                          cursor: 'text',
                        }}
                        placeholder={cfgAsesor.asesor
                          ? cfgAsesoresFiltrados.find(a => String(a.id_asesor) === String(cfgAsesor.asesor))?.nombre || 'Seleccionar asesor'
                          : 'Buscar asesor...'
                        }
                        value={cfgBusquedaAsesor}
                        onChange={(e) => {
                          setCfgBusquedaAsesor(e.target.value);
                          setCfgSelectAsesorAbierto(true);
                        }}
                        onFocus={() => setCfgSelectAsesorAbierto(true)}
                        onClick={() => setCfgSelectAsesorAbierto(true)}
                        disabled={cargandoAsesores}
                      />
                      {cfgBusquedaAsesor && (
                        <button
                          type="button"
                          onClick={() => {
                            setCfgBusquedaAsesor('');
                            setCfgAsesor(c => ({ ...c, asesor: '' }));
                            cargarConfigsAsesor('');
                          }}
                          style={{
                            position: 'absolute',
                            right: 12,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#9ca3af',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {cfgSelectAsesorAbierto && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 4,
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}>
                        {cfgAsesoresFiltrados.map((a) => (
                          <div
                            key={a.id_asesor}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: String(a.id_asesor) === String(cfgAsesor.asesor) ? 'var(--accent)' : 'transparent',
                              color: 'var(--text)',
                            }}
                            onClick={() => {
                              const value = String(a.id_asesor);
                              setCfgAsesor(c => ({ ...c, asesor: value }));
                              setCfgBusquedaAsesor(`${a.nombre}${a.cargo ? ` - ${a.cargo}` : ''}${a.nombre_sede ? ` - ${a.nombre_sede}` : ''}`);
                              setCfgSelectAsesorAbierto(false);
                              cargarConfigsAsesor(value);
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <div style={{ fontWeight: 500, color: 'var(--text)' }}>{a.nombre}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              {a.cargo} {a.nombre_sede ? `• ${a.nombre_sede}` : ''}
                            </div>
                          </div>
                        ))}
                        {cfgAsesoresFiltrados.length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                            {cargandoAsesores ? 'Cargando asesores...' : cfgSedeSeleccionada ? 'No hay asesores en esta sede' : 'Selecciona una sede primero'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Estado a configurar <span style={{ color: '#ef4444' }}>*</span></label>
                  <select style={styles.select} value={cfgAsesor.estado_id}
                    onChange={e => setCfgAsesor(c => ({ ...c, estado_id: e.target.value }))}>
                    <option value="">-- Selecciona un estado --</option>
                    {(estadosCat || []).map(e => (
                      <option key={e.id} value={e.id}>{e.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Estado de la configuración</label>
                  <select style={styles.select} value={String(cfgAsesor.activo)}
                    onChange={e => setCfgAsesor(c => ({ ...c, activo: e.target.value === 'true' }))}>
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button style={{ ...styles.submitBtn, marginTop: 0 }} onClick={async () => {
                    try {
                      if (!cfgAsesor.asesor || !cfgAsesor.estado_id) {
                        notify('Selecciona asesor y estado', 'warning');
                        return;
                      }
                      const color = toValidHex(cfgAsesor.color_hex_override);
                      if (color === 'INVALID') {
                        notify('Color inválido. Usa formato #RRGGBB', 'warning');
                        return;
                      }
                      const payload = {
                        asesor: Number(cfgAsesor.asesor),
                        estado_id: Number(cfgAsesor.estado_id),
                        activo: !!cfgAsesor.activo,
                        ...(cfgAsesor.limite_minutos !== '' && { limite_minutos: Number(cfgAsesor.limite_minutos) }),
                        ...(cfgAsesor.color_hex_override && cfgAsesor.color_hex_override.trim() !== '' && {
                          color_hex_override: cfgAsesor.color_hex_override.trim()
                        }),
                      };
                      await workforceApi.crearEstadoConfig(payload);
                      notify('Configuración guardada correctamente', 'success');
                      await cargarConfigsAsesor(Number(cfgAsesor.asesor));
                    } catch (e) {
                      let msg = 'No se pudo crear la configuración';
                      try { const j = JSON.parse(e.message); msg += `\n${JSON.stringify(j, null, 2)}`; } catch {}
                      console.error(e);
                      notify(msg, 'error');
                    }
                  }}>
                    Guardar configuración
                  </button>
                </div>
              </div>

              {cfgAsesor.asesor && (
                <div style={{ marginTop: 32 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16, fontFamily: "'Poppins', sans-serif" }}>
                    Configuraciones del asesor seleccionado
                  </h4>
                  {configsAsesor.length === 0 ? (
                    <div style={styles.emptyState}>
                      <div style={styles.emptyStateIcon}>📭</div>
                      <p style={styles.emptyStateText}>Sin configuraciones personalizadas</p>
                    </div>
                  ) : (
                    <div style={styles.tableContainer}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>ID</th>
                            <th style={styles.th}>Estado</th>
                            <th style={styles.th}>Límite</th>
                            <th style={styles.th}>Estado</th>
                            <th style={styles.th}>Activar/Desactivar</th>
                            <th style={styles.th}>Eliminar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {configsAsesor.map(cfg => (
                            <tr key={cfg.id} style={styles.tr}>
                              <td style={styles.td}>
                                <span style={{
                                  fontFamily: "'Poppins', sans-serif", fontSize: 13, fontWeight: 600,
                                  color: 'var(--text-secondary)', background: 'var(--bg)', padding: '4px 8px', borderRadius: 6
                                }}>#{cfg.id}</span>
                              </td>
                              <td style={styles.td}>
                                <span style={{ fontWeight: 600 }}>
                                  {cfg.estado?.nombre || cfg.estado_nombre || cfg.estado_id}
                                </span>
                              </td>
                              <td style={styles.td}>
                                <span style={{
                                  background: '#dbeafe', color: '#1e40af', padding: '4px 12px',
                                  borderRadius: 12, fontSize: 13, fontFamily: "'Poppins', sans-serif", fontWeight: 600
                                }}>
                                  {cfg.limite_minutos ?? '-'} min
                                </span>
                              </td>
                              <td style={styles.td}>
                                <span style={cfg.activo ? styles.badgeSuccess : styles.badgeDanger}>
                                  {cfg.activo ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td style={styles.td}>
                                <div style={styles.acciones}>
                                  <button
                                    style={{
                                      ...styles.btnEdit,
                                      background: cfg.activo ? '#dcfce7' : '#fee2e2',
                                      color: cfg.activo ? '#15803d' : '#dc2626',
                                      border: `1px solid ${cfg.activo ? '#86efac' : '#fca5a5'}`,
                                    }}
                                    title={cfg.activo ? 'Desactivar configuración' : 'Activar configuración'}
                                    onClick={async () => {
                                      try {
                                        await workforceApi.actualizarEstadoConfig(cfg.id, { activo: !cfg.activo });
                                        await cargarConfigsAsesor(cfgAsesor.asesor);
                                        notify(cfg.activo ? 'Configuración desactivada' : 'Configuración activada', 'success');
                                      } catch (e) {
                                        console.error(e);
                                        notify('No se pudo actualizar', 'error');
                                      }
                                    }}
                                  >
                                    {cfg.activo ? '✓ Activo' : '✕ Inactivo'}
                                  </button>
                                </div>
                              </td>
                              <td style={styles.td}>
                                <div style={styles.acciones}>
                                  <button
                                    disabled={cfg.estado?.nombre === "Disponible" || cfg.estado?.nombre === "Inactivo" || cfg.estado?.nombre === "Desconectado"}
                                    style={{
                                      ...styles.btnDelete,
                                      background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                                      opacity: (cfg.estado?.nombre === "Disponible" || cfg.estado?.nombre === "Inactivo" || cfg.estado?.nombre === "Desconectado") ? 0.4 : 1,
                                      cursor: (cfg.estado?.nombre === "Disponible" || cfg.estado?.nombre === "Inactivo" || cfg.estado?.nombre === "Desconectado") ? 'not-allowed' : 'pointer',
                                    }}
                                    title={(cfg.estado?.nombre === "Disponible" || cfg.estado?.nombre === "Inactivo" || cfg.estado?.nombre === "Desconectado")
                                      ? "No se puede eliminar este estado" : "Eliminar configuración"}
                                    onClick={async () => {
                                      if (cfg.estado?.nombre === "Disponible" || cfg.estado?.nombre === "Inactivo" || cfg.estado?.nombre === "Desconectado") {
                                        notify("No puedes eliminar configuraciones con este estado", "error");
                                        return;
                                      }
                                      if (!window.confirm("¿Estás seguro de que deseas eliminar esta configuración?")) return;
                                      try {
                                        const response = await fetch(
                                          `https://hrs-api.distritec.cloud/api/eliminar/estado/${cfg.id}/`,
                                          { method: "DELETE", headers: { "Accept": "application/json" }, mode: "cors" }
                                        );
                                        if (!response.ok && response.status !== 204) {
                                          const errorText = await response.text().catch(() => 'Error desconocido');
                                          throw new Error(`Error al eliminar: ${errorText}`);
                                        }
                                        await cargarConfigsAsesor(cfgAsesor.asesor);
                                        notify("Configuración eliminada exitosamente", "success");
                                      } catch (e) {
                                        console.error('Error al eliminar configuración:', e);
                                        notify(e?.message || "No se pudo eliminar la configuración", "error");
                                      }
                                    }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}